import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import {
  canAccess,
  type AccessContext,
  type Profile,
  type Organization,
  type UserRole,
} from '@klothiq/domain';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface AuthState {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  organization: Organization | null;
  role: UserRole | null;
  access: AccessContext | null;
  accessError: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}
interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  can: (permission: string) => boolean;
}
const initial: AuthState = {
  session: null,
  user: null,
  profile: null,
  organization: null,
  role: null,
  access: null,
  accessError: null,
  isLoading: true,
  isAuthenticated: false,
};
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initial);
  const queryClient = useQueryClient();
  const identity = useRef('');
  const generation = useRef(0);
  const refresh = useCallback(
    async (session: Session | null) => {
      const request = ++generation.current;
      if (!session) {
        await queryClient.cancelQueries();
        queryClient.clear();
        identity.current = '';
        if (request === generation.current) setState({ ...initial, isLoading: false });
        return;
      }
      try {
        const { data: access, error } = await supabase.rpc('get_access_context');
        if (error || !access)
          throw new Error(
            'Your account access could not be loaded. Try again or contact your Super Admin.',
          );
        if (request !== generation.current) return;
        const fingerprint = `${session.user.id}:${access.organization_id}:${access.access_version}:${access.ready}`;
        if (fingerprint !== identity.current) {
          await queryClient.cancelQueries();
          queryClient.clear();
          identity.current = fingerprint;
        }
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', session.user.id)
          .single();
        if (profileError || !profile)
          throw new Error('This login is not linked to an available person.');
        const orgResult = access.ready
          ? await supabase
              .from('organizations')
              .select('*')
              .eq('id', access.organization_id)
              .single()
          : { data: null };
      if (access.ready && !orgResult.data) throw new Error('Your organization could not be loaded. Try again.');
        if (request !== generation.current) return;
        setState({
          session,
          user: session.user,
          profile: profile as Profile,
          organization: orgResult.data as Organization | null,
          role: access.role,
          access: access as AccessContext,
          accessError: null,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch (error) {
        if (request !== generation.current) return;
        await queryClient.cancelQueries();
        queryClient.clear();
        setState({
          ...initial,
          session,
          user: session.user,
          isAuthenticated: true,
          isLoading: false,
          accessError: error instanceof Error ? error.message : 'Account access is unavailable.',
        });
      }
    },
    [queryClient],
  );
  useEffect(() => {
    let disposed = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!disposed) void refresh(data.session);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Database work runs after Auth releases its callback lock.
      setState((prev) => ({
        ...prev,
        session,
        user: session?.user ?? null,
        isAuthenticated: !!session,
        ...(event === 'SIGNED_OUT' || (event === 'SIGNED_IN' && prev.user?.id !== session?.user.id)
          ? { access: null, profile: null, organization: null, isLoading: !!session }
          : {}),
      }));
      setTimeout(() => {
        if (!disposed) void refresh(session);
      }, 0);
    });
    return () => {
      disposed = true;
      generation.current++;
      subscription.unsubscribe();
    };
  }, [refresh]);
  useEffect(() => {
    if (!state.session) return;
    const refreshAccess = () => {
      if (document.visibilityState === 'visible') void refresh(state.session);
    };
    const timer = window.setInterval(refreshAccess, 30_000);
    window.addEventListener('focus', refreshAccess);
    document.addEventListener('visibilitychange', refreshAccess);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refreshAccess);
      document.removeEventListener('visibilitychange', refreshAccess);
    };
  }, [state.session, refresh]);
  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    if (error) throw error;
  }, []);
  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) throw error;
    await refresh(null);
  }, [refresh]);
  const refreshProfile = useCallback(async () => {
    await refresh(state.session);
  }, [refresh, state.session]);
  const can = useCallback(
    (permission: string) => {
      if (permission === 'owner') return !!state.access?.ready && state.access.role === 'owner';
      if (permission === 'reports')
        return (
          !!state.access?.ready &&
          (state.access.role === 'owner' ||
            state.access.permissions.some((p) => p.startsWith('report_') && p.endsWith('.view')))
        );
      return canAccess(state.access, permission);
    },
    [state.access],
  );
  return (
    <AuthContext.Provider value={{ ...state, signIn, signOut, refreshProfile, can }}>
      {children}
    </AuthContext.Provider>
  );
}
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
